/*
  <complete-the-look> - product page "Complete the look" (master list #29/#30,
  28 Sep 2026). Markup: snippets/complete-the-look-item.liquid +
  sections/main-product.liquid. Design approved by adam from
  complete-the-look-280926/mockup.html.

  - Each suggestion's size follows the main product's size ("Matched to your
    size") unless the shopper picks a different size on that suggestion; picking
    the matching size again puts it back to following. Combined sizes such as
    "XS/S" match either part. Missing / sold-out sizes are explained instead of
    silently picking another size.
  - ADD adds that suggestion; ADD THE SET (only rendered when there is exactly
    one suggestion) adds the main product + the suggestion in one request.
    Both refresh and open the cart drawer like the main ADD TO BAG.
  - 3-image mini carousel per suggestion (arrows + progress line). The photo is
    not a link (mobile swipes must not leave the page).

  product-recommendations injects this markup after load, so everything is set
  up in connectedCallback (children exist by then).
*/
(() => {
  if (customElements.get('complete-the-look')) return;

  const splitSize = (size) =>
    String(size || '')
      .toUpperCase()
      .split('/')
      .map((s) => s.trim())
      .filter(Boolean);
  const sizesMatch = (a, b) => {
    const A = splitSize(a);
    const B = splitSize(b);
    return A.length > 0 && A.some((s) => B.includes(s));
  };

  // Same rule as the main product (assets/default-size.js, #53): if the main size is
  // sold out or missing on a suggestion, use the next size up in stock, else the
  // nearest smaller size in stock.
  const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const sizeRank = (size) => SIZE_ORDER.indexOf(splitSize(size)[0]);
  const fallbackButton = (buttons, size) => {
    const target = sizeRank(size);
    if (target < 0) return null;
    const inStock = buttons.filter((b) => !b.classList.contains('is-soldout') && sizeRank(b.dataset.ctlSize) > -1);
    const up = inStock
      .filter((b) => sizeRank(b.dataset.ctlSize) > target)
      .sort((a, b) => sizeRank(a.dataset.ctlSize) - sizeRank(b.dataset.ctlSize));
    if (up.length) return up[0];
    const down = inStock
      .filter((b) => sizeRank(b.dataset.ctlSize) < target)
      .sort((a, b) => sizeRank(b.dataset.ctlSize) - sizeRank(a.dataset.ctlSize));
    return down[0] || null;
  };

  class CompleteTheLook extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.sectionId = this.dataset.sectionId;
      this.mainVariantId = this.dataset.mainVariantId;
      this.mainPrice = parseInt(this.dataset.mainPrice, 10) || 0;
      this.mainAvailable = this.dataset.mainAvailable === 'true';
      this.items = Array.from(this.querySelectorAll('[data-ctl-item]'));
      this.setButton = this.querySelector('[data-ctl-set]');
      this.setNote = this.querySelector('[data-ctl-set-note]');

      this.items.forEach((item) => this.setupItem(item));
      this.querySelectorAll('[data-ctl-carousel]').forEach((el) => this.setupCarousel(el));

      if (this.setButton) this.setButton.addEventListener('click', () => this.addSet());

      // Same press feedback as ADD TO BAG on touch devices (iOS never fires :active).
      this.addEventListener(
        'touchstart',
        (event) => {
          const button = event.target.closest('[data-ctl-add], [data-ctl-set]');
          if (!button) return;
          button.classList.add('tap-active');
          setTimeout(() => button.classList.remove('tap-active'), 150);
        },
        { passive: true }
      );

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
          const variant = event.data.variant;
          if (variant) {
            this.mainVariantId = String(variant.id);
            this.mainPrice = variant.price;
            this.mainAvailable = !!variant.available;
          }
          this.syncAll();
        });
        // Bag changed anywhere (main ADD TO BAG, drawer remove/qty): refresh ADDED states.
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.loadCart());
      }
      const picker = document.getElementById(`variant-selects-${this.sectionId}`);
      if (picker) {
        this.onPickerChange = () => setTimeout(() => this.syncAll(), 0);
        picker.addEventListener('change', this.onPickerChange);
      }

      this.cartIds = new Set();
      this.syncAll();
      this.loadCart();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.unsubscribeCart) this.unsubscribeCart();
      const picker = document.getElementById(`variant-selects-${this.sectionId}`);
      if (picker && this.onPickerChange) picker.removeEventListener('change', this.onPickerChange);
    }

    /* ---------- main product ---------- */

    mainSize() {
      const picker = document.getElementById(`variant-selects-${this.sectionId}`);
      if (!picker) return null;
      const fieldset = Array.from(picker.querySelectorAll('fieldset')).find((fs) => {
        const legend = fs.querySelector('legend');
        return legend && /size/i.test(legend.textContent);
      });
      if (fieldset) {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      }
      const select = Array.from(picker.querySelectorAll('select')).find((s) => {
        const label = picker.querySelector(`label[for="${s.id}"]`);
        return label && /size/i.test(label.textContent);
      });
      return select ? select.value : null;
    }

    mainVariant() {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      const input = form && form.querySelector('input[name="id"]');
      return (input && input.value) || this.mainVariantId;
    }

    /* ---------- suggestions ---------- */

    setupItem(item) {
      item.manual = false;
      item.sizeButtons = Array.from(item.querySelectorAll('[data-ctl-size]'));
      item.hint = item.querySelector('[data-ctl-hint]');
      item.addButton = item.querySelector('[data-ctl-add]');

      item.sizeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.classList.contains('is-soldout')) return;
          this.select(item, button);
          const matched = sizesMatch(this.mainSize(), button.dataset.ctlSize);
          item.manual = !matched;
          this.setHint(item, matched ? 'Matched to your size' : 'Your choice');
          this.updateSet();
        });
      });

      item.addButton.addEventListener('click', () => {
        const variantId = this.itemVariant(item);
        if (!variantId) {
          this.setHint(item, 'Choose a size first');
          return;
        }
        this.addToCart([{ id: variantId, quantity: 1 }], item.addButton, item.hint);
      });
    }

    select(item, button) {
      item.sizeButtons.forEach((b) => {
        const on = b === button;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      item.selected = button || null;
      item.addButton.disabled = item.hasAttribute('data-no-sizes') ? item.dataset.available !== 'true' : !button;
      this.refreshAdded();
    }

    setHint(item, text) {
      if (item.hint) item.hint.textContent = text || '';
    }

    itemVariant(item) {
      if (item.hasAttribute('data-no-sizes')) return item.dataset.available === 'true' ? item.dataset.variantId : null;
      return item.selected ? item.selected.dataset.variantId : null;
    }

    itemPrice(item) {
      const source = item.selected || item;
      return parseInt(source.dataset.price, 10) || 0;
    }

    syncItem(item, size) {
      if (item.hasAttribute('data-no-sizes')) {
        this.select(item, null);
        this.setHint(item, item.dataset.available === 'true' ? '' : 'Sold out');
        return;
      }
      if (!size) {
        this.select(item, null);
        this.setHint(item, 'Choose a size');
        return;
      }
      const match = item.sizeButtons.find((b) => sizesMatch(size, b.dataset.ctlSize));
      if (match && !match.classList.contains('is-soldout')) {
        this.select(item, match);
        this.setHint(item, 'Matched to your size');
        return;
      }
      const reason = match ? `${match.dataset.ctlSize} sold out` : `No ${size} in this style`;
      const alternative = fallbackButton(item.sizeButtons, size);
      if (alternative) {
        this.select(item, alternative);
        this.setHint(item, `${reason}, showing ${alternative.dataset.ctlSize}`);
      } else {
        this.select(item, null);
        this.setHint(item, match ? 'Sold out' : reason);
      }
    }

    syncAll() {
      const size = this.mainSize();
      this.items.forEach((item) => {
        if (!item.manual) this.syncItem(item, size);
      });
      this.updateSet();
    }

    /* ---------- ADD THE SET (single suggestion only) ---------- */

    updateSet() {
      if (!this.setButton) return;
      const item = this.items[0];
      const itemVariant = item && this.itemVariant(item);
      if (!this.mainAvailable) {
        this.setButton.disabled = true;
        this.setButton.textContent = 'Add the set';
        this.setNote.textContent = 'Your size is sold out in this style';
        return;
      }
      if (!itemVariant) {
        this.setButton.disabled = true;
        this.setButton.textContent = 'Add the set';
        this.setNote.textContent = 'Choose a size for the matching piece to add the set';
        return;
      }
      this.setButton.disabled = false;
      this.setButton.textContent = `Add the set (2 items) - ${this.formatMoney(this.mainPrice + this.itemPrice(item))}`;
      this.setNote.textContent = '';
      this.refreshAdded();
    }

    addSet() {
      const item = this.items[0];
      const itemVariant = item && this.itemVariant(item);
      const mainVariant = this.mainVariant();
      if (!itemVariant || !mainVariant || !this.mainAvailable) return;
      this.addToCart(
        [
          { id: mainVariant, quantity: 1 },
          { id: itemVariant, quantity: 1 },
        ],
        this.setButton,
        this.setNote
      );
    }

    /* ---------- cart ---------- */

    addToCart(lines, button, messageEl) {
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { items: lines.map((l) => ({ id: Number(l.id), quantity: l.quantity })) };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
      }
      const label = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';

      fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (messageEl) messageEl.textContent = response.description || response.message || 'Sorry, that could not be added';
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, { source: 'complete-the-look', errors: response.description, message: response.message });
            }
            return;
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          const last = lines[lines.length - 1];
          const lastItem = (response.items || []).find((i) => String(i.variant_id) === String(last.id)) || {};
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'complete-the-look', productVariantId: last.id, cartData: response });
          }
          lines.forEach((l) => this.cartIds.add(String(l.id)));
          cart.renderContents({ ...response, id: Number(last.id), key: lastItem.key });
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        })
        .catch((error) => {
          console.error(error);
          if (messageEl) messageEl.textContent = 'Sorry, something went wrong. Please try again.';
        })
        .finally(() => {
          button.textContent = label;
          button.disabled = false;
          if (button === this.setButton) this.updateSet();
          else this.items.forEach((item) => this.select(item, item.selected));
          this.refreshAdded();
        });
    }

    /* ---------- ADDED state (adam, 28 Sep) ---------- */

    // Which variants are in the bag right now (source of truth: /cart.js).
    loadCart() {
      fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          this.cartIds = new Set((cart.items || []).map((line) => String(line.variant_id)));
          this.refreshAdded();
        })
        .catch(() => {});
    }

    // ADD -> "Added" (greyed) when that exact size is in the bag; back to ADD if the
    // shopper picks another size or removes it. Same for ADD THE SET ("Set added").
    refreshAdded() {
      if (!this.cartIds) return;
      this.items.forEach((item) => {
        const variant = this.itemVariant(item);
        const added = !!variant && this.cartIds.has(String(variant));
        if (added) {
          item.addButton.textContent = 'Added';
          item.addButton.disabled = true;
          item.addButton.classList.add('is-added');
        } else if (item.addButton.classList.contains('is-added')) {
          item.addButton.classList.remove('is-added');
          item.addButton.textContent = 'Add';
          item.addButton.disabled = !variant;
        }
      });
      if (this.setButton && this.items[0]) {
        const itemVariant = this.itemVariant(this.items[0]);
        const both = itemVariant && this.cartIds.has(String(itemVariant)) && this.cartIds.has(String(this.mainVariant()));
        if (both) {
          this.setButton.textContent = 'Set added';
          this.setButton.disabled = true;
          this.setButton.classList.add('is-added');
          if (this.setNote) this.setNote.textContent = '';
        } else if (this.setButton.classList.contains('is-added')) {
          this.setButton.classList.remove('is-added');
          this.updateSet();
        }
      }
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      const [whole, decimals] = value.split('.');
      const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const withDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return withCommas;
          case 'amount_with_comma_separator':
            return `${withDots},${decimals}`;
          case 'amount_no_decimals_with_comma_separator':
            return withDots;
          default:
            return decimals === '00' ? withCommas : `${withCommas}.${decimals}`;
        }
      });
    }

    /* ---------- mini carousel ---------- */

    setupCarousel(el) {
      const track = el.querySelector('[data-ctl-track]');
      const prev = el.querySelector('[data-ctl-prev]');
      const next = el.querySelector('[data-ctl-next]');
      const thumb = el.querySelector('[data-ctl-thumb]');
      if (!track || !prev || !next) return;
      const count = track.children.length;
      const index = () => Math.round(track.scrollLeft / (track.clientWidth || 1));
      const update = () => {
        const i = index();
        prev.hidden = i <= 0;
        next.hidden = i >= count - 1;
        if (thumb) thumb.style.transform = `translateX(${i * 100}%)`;
      };
      const go = (i) => track.scrollTo({ left: Math.max(0, Math.min(i, count - 1)) * track.clientWidth, behavior: 'smooth' });
      prev.addEventListener('click', (event) => {
        event.preventDefault();
        go(index() - 1);
      });
      next.addEventListener('click', (event) => {
        event.preventDefault();
        go(index() + 1);
      });
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      update();
    }
  }

  customElements.define('complete-the-look', CompleteTheLook);
})();
